import type { IMessagePollInput, IPoll } from './service/typings';

export const MESSAGE_POLL_LIMITS = {
  questionMin: 1,
  questionMax: 200,
  optionsMin: 2,
  optionsMax: 4,
  optionTextMin: 1,
  optionTextMax: 65,
} as const;

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** Poll length in whole minutes. Spec: feed, "poll length picker". */
export const MESSAGE_POLL_DURATION = {
  minMinutes: 5,
  maxDays: 7,
  maxMinutes: 7 * MINUTES_PER_DAY,
  defaultMinutes: MINUTES_PER_DAY,
} as const;

export type TPollDurationPart = 'days' | 'hours' | 'minutes';

export type TPollDurationParts = Record<TPollDurationPart, number>;

const range = (count: number) => Array.from({ length: count }, (_, index) => index);

/** The values each picker offers. */
export const MESSAGE_POLL_DURATION_CHOICES: Record<TPollDurationPart, readonly number[]> = {
  days: range(MESSAGE_POLL_DURATION.maxDays + 1),
  hours: range(24),
  minutes: range(60),
};

export function pollDurationParts(totalMinutes: number): TPollDurationParts {
  const total = Math.max(0, Math.floor(totalMinutes));
  return {
    days: Math.floor(total / MINUTES_PER_DAY),
    hours: Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR),
    minutes: total % MINUTES_PER_HOUR,
  };
}

/** True when the picker locks hours and minutes at 0: the poll already runs the maximum. */
export function isPollDurationAtMaxDays(totalMinutes: number): boolean {
  return pollDurationParts(totalMinutes).days >= MESSAGE_POLL_DURATION.maxDays;
}

/** The total after one picker changes. Choosing 7 days sets hours and minutes to 0. */
export function setPollDurationPart(
  totalMinutes: number,
  part: TPollDurationPart,
  value: number,
): number {
  const parts = { ...pollDurationParts(totalMinutes), [part]: value };
  if (parts.days >= MESSAGE_POLL_DURATION.maxDays) {
    return MESSAGE_POLL_DURATION.maxMinutes;
  }
  return parts.days * MINUTES_PER_DAY + parts.hours * MINUTES_PER_HOUR + parts.minutes;
}

/** A poll's length for analytics: the stored minutes, the stored hours of an older poll, or its window. */
export function pollDurationMinutesOf(poll: IPoll): number | undefined {
  const settings = poll.settings ?? {};
  if (typeof settings.duration_minutes === 'number') return settings.duration_minutes;
  if (typeof settings.duration_hours === 'number') return settings.duration_hours * MINUTES_PER_HOUR;
  if (!poll.start_date || !poll.end_date) return undefined;
  const window = Date.parse(poll.end_date) - Date.parse(poll.start_date);
  return Number.isFinite(window) ? Math.round(window / 60_000) : undefined;
}

export interface IMessagePollDraftOption {
  text: string;
  key?: string;
}

export interface IMessagePollDraft {
  question: string;
  options: IMessagePollDraftOption[];
  duration_minutes: number;
}

export type TMessagePollDraftField = 'question' | 'options' | 'duration_minutes';

export type TMessagePollDraftErrors = Partial<Record<TMessagePollDraftField, string>>;

const charCount = (text: string) => Array.from(text).length;

const toMs = (now: Date | number) => (typeof now === 'number' ? now : now.getTime());

const percentOf = (votes: number, total: number) =>
  total > 0 ? Math.round((votes * 1000) / total) / 10 : 0;

export function validateMessagePollDraft(draft: IMessagePollDraft): TMessagePollDraftErrors {
  const errors: TMessagePollDraftErrors = {};

  const question = draft.question.trim();
  if (charCount(question) < MESSAGE_POLL_LIMITS.questionMin) {
    errors.question = 'Add a question.';
  } else if (charCount(question) > MESSAGE_POLL_LIMITS.questionMax) {
    errors.question = 'Questions can have up to 200 characters.';
  }

  const texts = draft.options.map((option) => option.text.trim()).filter((text) => text.length > 0);
  if (texts.length < MESSAGE_POLL_LIMITS.optionsMin) {
    errors.options = 'Add at least 2 options.';
  } else if (texts.length > MESSAGE_POLL_LIMITS.optionsMax) {
    errors.options = 'Polls can have up to 4 options.';
  } else if (texts.some((text) => charCount(text) > MESSAGE_POLL_LIMITS.optionTextMax)) {
    errors.options = 'Options can have up to 65 characters.';
  } else if (new Set(texts.map((text) => text.toLowerCase())).size !== texts.length) {
    errors.options = 'Each option must be different.';
  }

  const minutes = draft.duration_minutes;
  if (!Number.isInteger(minutes) || minutes < MESSAGE_POLL_DURATION.minMinutes) {
    errors.duration_minutes = 'A poll must run for at least 5 minutes.';
  } else if (minutes > MESSAGE_POLL_DURATION.maxMinutes) {
    errors.duration_minutes = 'A poll can run for up to 7 days.';
  }

  return errors;
}

export function toMessagePollInput(draft: IMessagePollDraft): IMessagePollInput {
  return {
    question: draft.question.trim(),
    options: draft.options
      .map((option) => option.text.trim())
      .filter((text) => text.length > 0)
      .map((text) => ({ text })),
    duration_minutes: draft.duration_minutes,
  };
}

export function isPollOpen(poll: IPoll, now: Date | number): boolean {
  if (!poll.is_active) return false;
  if (!poll.end_date) return true;
  return Date.parse(poll.end_date) > toMs(now);
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const countOf = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/** The "X" in "ends in X": the nearest whole minute, hour or day. Spec: feed. */
export function formatPollTimeLeft(endDate: string | undefined, now: number): string | null {
  if (!endDate) return null;
  const remaining = Date.parse(endDate) - now;
  if (!Number.isFinite(remaining) || remaining <= 0) return null;
  const minutes = Math.max(1, Math.round(remaining / MINUTE_MS));
  if (minutes < 60) return countOf(minutes, 'minute');
  const hours = Math.round(remaining / HOUR_MS);
  if (hours < 24) return countOf(hours, 'hour');
  return countOf(Math.round(remaining / DAY_MS), 'day');
}

export interface IPollOptionTally {
  id: string;
  text: string;
  position: number;
  votes: number | null;
  percentage: number | null;
}

const optionsInPositionOrder = (poll: IPoll) =>
  poll.options
    .map((option, index) => ({ option, index, position: option.position ?? index + 1 }))
    .sort((a, b) => a.position - b.position || a.index - b.index);

export function pollOptionTallies(poll: IPoll): IPollOptionTally[] {
  const rows = poll.results
    ? new Map((poll.results.options ?? []).map((row) => [row.option_id, row]))
    : null;

  return optionsInPositionOrder(poll).map(({ option, position }) => {
    const row = rows && option.id ? rows.get(option.id) : undefined;
    return {
      id: option.id ?? '',
      text: option.text,
      position,
      votes: rows ? row?.votes ?? 0 : null,
      percentage: rows ? row?.percentage ?? 0 : null,
    };
  });
}

export interface IPollCounts {
  total_votes: number;
  options: { option_id: string; votes: number; percentage?: number }[];
}

type TOptionRow = { votes: number; percentage?: number };

const buildResults = (
  poll: IPoll,
  total: number,
  rowOf: (optionId: string) => TOptionRow | undefined,
) => ({
  total_votes: total,
  options: optionsInPositionOrder(poll).map(({ option }) => {
    const row = rowOf(option.id ?? '');
    const votes = row?.votes ?? 0;
    return {
      option_id: option.id ?? '',
      text: option.text,
      votes,
      percentage: row?.percentage ?? percentOf(votes, total),
    };
  }),
});

export function applyOptimisticPollVote(
  poll: IPoll | null | undefined,
  optionId: string,
): IPoll | null | undefined {
  if (!poll || poll.user_has_voted) return poll;
  const chosen = poll.options.find((option) => option.id === optionId);
  if (!chosen) return poll;

  const next: IPoll = {
    ...poll,
    user_has_voted: true,
    user_can_vote: false,
    user_selected_option: { id: optionId, text: chosen.text },
  };
  if (!poll.results) return next;

  const before = new Map(pollOptionTallies(poll).map((tally) => [tally.id, tally.votes ?? 0]));
  next.results = buildResults(poll, poll.results.total_votes + 1, (id) => ({
    votes: (before.get(id) ?? 0) + (id === optionId ? 1 : 0),
  }));
  return next;
}

export function mergePollCounts(
  poll: IPoll | null | undefined,
  pollId: string,
  counts: IPollCounts,
): IPoll | null | undefined {
  if (!poll || poll.id !== pollId) return poll;
  if (poll.results && counts.total_votes <= poll.results.total_votes) return poll;

  const rows = new Map(counts.options.map((row) => [row.option_id, row]));
  return { ...poll, results: buildResults(poll, counts.total_votes, (id) => rows.get(id)) };
}

export type TPollCardState =
  | 'none'
  | 'hidden'
  | 'draft'
  | 'preview'
  | 'read-only'
  | 'pending'
  | 'ended'
  | 'voted'
  | 'open';

export interface IPollCardStateInput {
  poll: IPoll | null | undefined;
  platform: 'web' | 'native';
  now: Date | number;
  blocked?: boolean;
  moderationView?: boolean;
  draft?: boolean;
  preview?: boolean;
  readOnly?: boolean;
  pending?: boolean;
}

export function pollCardState(input: IPollCardStateInput): TPollCardState {
  const { poll } = input;
  if (!poll) return 'none';
  if (input.blocked && !input.moderationView) return 'hidden';
  if (input.draft) return input.platform === 'web' ? 'draft' : 'none';
  if (input.preview) return 'preview';
  if (input.readOnly || input.moderationView) return 'read-only';
  if (input.pending) return 'pending';
  if (!isPollOpen(poll, input.now)) return 'ended';
  if (poll.user_has_voted) return 'voted';
  return 'open';
}

export function formatPollPreview(question: string): string {
  return `📊 ${question.trim()}`;
}

export type TPollVoteFailureAction =
  | 'undo'
  | 'close-poll'
  | 'write-server-poll'
  | 'refetch-message';

export interface IPollVoteFailure {
  action: TPollVoteFailureAction;
  message: string;
}

export const CREW_REMOVED_ERROR = 'You have been removed from this Crew.';

export function pollVoteFailure(
  status?: number | string,
  code?: string | null,
  serverError?: string | null,
): IPollVoteFailure {
  if (status === 403) {
    if (code !== 'crew_membership_required') {
      return { action: 'undo', message: "You can't vote in this poll." };
    }
    return {
      action: 'undo',
      message:
        serverError?.trim() === CREW_REMOVED_ERROR
          ? 'You were removed from this Crew. Contact the Crew admin if you have questions.'
          : 'To vote, you need to rejoin the crew.',
    };
  }
  if (status === 422 && code === 'poll_closed') {
    return { action: 'close-poll', message: 'This poll has ended.' };
  }
  if (status === 422 && code === 'poll_already_voted') {
    return { action: 'write-server-poll', message: 'You already voted in this poll.' };
  }
  if ((status === 422 && code === 'poll_option_invalid') || status === 404) {
    return { action: 'refetch-message', message: 'This poll is no longer available.' };
  }
  return { action: 'undo', message: 'Your vote was not counted. Try again.' };
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const asId = (value: unknown): string | null => {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

export function parsePollEvent(
  payload: unknown,
): { messageId: string; pollId: string; counts: IPollCounts } | null {
  const data = asRecord(payload);
  if (!data) return null;
  const messageId = asId(data.message_id);
  const pollId = asId(data.poll_id);
  const results = asRecord(data.results);
  if (!messageId || !pollId || !results) return null;

  const total = results.total_votes;
  if (typeof total !== 'number' || !Number.isFinite(total)) return null;

  const options = (Array.isArray(results.options) ? results.options : []).flatMap((entry) => {
    const row = asRecord(entry);
    const optionId = row && asId(row.option_id);
    if (!row || !optionId || typeof row.votes !== 'number' || !Number.isFinite(row.votes))
      return [];
    const percentage =
      typeof row.percentage === 'number' && Number.isFinite(row.percentage)
        ? { percentage: row.percentage }
        : {};
    return [{ option_id: optionId, votes: row.votes, ...percentage }];
  });

  return { messageId, pollId, counts: { total_votes: total, options } };
}

export function readPollInvalidError(error: unknown): { field: string; error: string } | null {
  const failure = asRecord(error);
  const body = asRecord(failure?.data);
  if (!failure || failure.status !== 422 || !body || body.code !== 'poll_invalid') return null;
  return {
    field: typeof body.field === 'string' ? body.field : '',
    error: typeof body.error === 'string' ? body.error : '',
  };
}
